<template>
  <div class="calendar-wrapper">
    <!-- Header -->
    <div class="text-center text-white rangeCalender-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="angle black-angle position-relative" id="close" @click="onCloseCustomDate"></div>
        <div :class="['save-btn', { disabled: !startDate }]" @click="saveRange">Save</div>
      </div>
      <p class="mb-0">Select a Date Range</p>
      <h3 v-if="startDate && endDate">
        {{ formatDate(startDate) }} → {{ formatDate(endDate) }}
      </h3>
      <h3 v-else>Select start - end date</h3>
      <button
        type="button"
        class="btn btn-sm"
        @click="toggleEditRange"
        style="background:#efefef;border-radius:8px;padding:.25rem .6rem;margin-top:.5rem; display: none !important"
        aria-label="Edit date range"
        v-if="!isEditingRange"
        >
        <span>✏️ Edit</span>
        <!-- <span v-else>✖ Close</span> -->
      </button>
    </div> 

     <!-- Manual inputs (MM/DD/YYYY) -->
    <div v-if="isEditingRange" class="px-3 mt-2">
      <!-- <div class="text-uppercase mb-2" style="font-size:12px;opacity:.7;">Start date – End date</div> -->
      <div class="d-flex gap-2 flex-wrap">
        <!-- Start -->
        <div style="min-width:220px;">
          <label class="mb-1" style="font-size:12px;opacity:.7;">Start date</label>
          <input
            type="text"
            class="trade-formControl"
            v-model="startInput"
            placeholder="MM/DD/YYYY"
            inputmode="numeric"
            maxlength="10"
            autocomplete="off"
            @keydown="onKeydownMDY"
            @input="coerceStart"
            @change="coerceStart"
            @blur="touched.start = true"
             :class="{ 'is-invalid': showErr('start') }"
          />
          <small v-if="showErr('start')" class="text-danger">{{ rangeErrors.start }}</small>
        </div>

        <!-- End -->
        <div style="min-width:220px;">
          <label class="mb-1" style="font-size:12px;opacity:.7;">End date</label>
          <input
            type="text"
            class="trade-formControl"
            v-model="endInput"
            placeholder="MM/DD/YYYY"
            inputmode="numeric"
            maxlength="10"
            autocomplete="off"
            @keydown="onKeydownMDY"
            @input="coerceEnd"
            @change="coerceEnd"
            @blur="touched.end = true"
            :class="{ 'is-invalid': showErr('end') }"
          />
          <small v-if="showErr('end')" class="text-danger">{{ rangeErrors.end }}</small>
        </div>
      </div>
    </div>
    
    <!-- Calendar months -->
    <div class="px-3 months-container" ref="monthsContainer" v-if="!isEditingRange">
      <div v-for="m in months"
        :key="`${m.year}-${m.month}`"
        class="month"
      >
        <h4>{{ monthNames[m.month - 1] }} {{ m.year }}</h4>
        <div class="days-grid">
          <div v-for="day in getDaysInMonth(m.year, m.month)"
            :key="day.date.toISOString()"
            class="day"
            :class="{ disabled: isFutureStamp(day.stamp), selected: isSelected(day.date), inRange: isInRange(day.date) }"
            @click="selectDate(day.date)"
          >
            {{ day.day }}
          </div>
        </div>
      </div>
      <!-- sentinel that triggers loading previous years until 2019 -->
      <div ref="bottomSentinel" class="io-sentinel"></div> 
    </div>
  </div>
</template>
 
<script>
export default {
  props: {
    onCloseCustomDate: {
      type: Function,
      required: true,
    },
  },
   emits: ['apply-range'],
  data() {
    const today = new Date();
    return {
      currentYear: today.getFullYear(),
      currentMonth: today.getMonth() + 1, // 1–12
      minYearLoaded: today.getFullYear(), 
      minYearLimit: 2019,
      months: [],
      startDate: null,
      endDate: null,
      startStamp: null,
      endStamp: null,
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
     io: null, // IntersectionObserver
     // cache today's midnight stamp
     todayStamp: null,

      // manual inputs
      isEditingRange: false,
      startInput: '',
      endInput: '',
      rangeErrors: { start: '', end: '' },
      touched: { start: false, end: false },
      formatHint: 'Invalid format. Use MM/DD/YYYY (e.g., 08/26/2025)',

    // route/auth
    token: '',
    };
  },
  computed: {
    canSave() {
      // start must be valid; end is optional but if present must be valid too
      const startOk = !!this.startDate && !this.rangeErrors.start;
      const endOk = (!this.endInput && !this.endDate) || (!!this.endDate && !this.rangeErrors.end);
      return startOk && endOk;
    }
  },
    mounted() {
        this.token = String((this.$route.query.token ?? this.$route.params.token ?? '')).trim()
        this.initMonths();
        this.$nextTick(this.setupInfiniteScroll);
    },
    beforeUnmount() {
        if (this.io) this.io.disconnect();
    },
  methods: {
    /* helpers to show errors immediately while typing */
    showErr(which) {
      const inputVal = which === 'start' ? this.startInput : this.endInput;
      return (this.touched[which] || inputVal.length > 0) && !!this.rangeErrors[which];
    },
     sod(d) {
      // start-of-day local
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },
    toYMD(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    toMDY(d) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },
    daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }, // m: 1..12
    getTodayStamp() {
      if (this.todayStamp == null) {
        this.todayStamp = this.sod(new Date()).getTime();
      }
      return this.todayStamp;
    },
       /* ---------- months loading ---------- */
    initMonths() {
      // load current year from currentMonth down to Jan
      const firstBatch = [];
      for (let m = this.currentMonth; m >= 1; m--) {
        firstBatch.push({ year: this.currentYear, month: m });
      }
      this.months = firstBatch;
      this.minYearLoaded = this.currentYear;
    },
    loadMoreMonths() {
        // already at/under the limit? stop
        if (this.minYearLoaded <= this.minYearLimit) {
        this.reachedLimit = true;
        this.disconnectObserver();
        return;
        }

      // append previous year (Dec..Jan)
      const prevYear = this.minYearLoaded - 1;

      // if adding prevYear would go before the limit, stop
    if (prevYear < this.minYearLimit) {
      this.reachedLimit = true;
      this.disconnectObserver();
      return;
    }
      const batch = [];
      for (let m = 12; m >= 1; m--) batch.push({ year: prevYear, month: m });
      this.months.push(...batch);
      this.minYearLoaded = prevYear;
      // if we just reached the limit year, stop observing
     if (this.minYearLoaded <= this.minYearLimit) {
      this.reachedLimit = true;
      this.disconnectObserver();
     }
    },
    disconnectObserver() {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
   },

    setupInfiniteScroll() {
      const root = this.$refs.monthsContainer;
      const end = this.$refs.bottomSentinel;
      if (!root || !end) return;

      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.loadMoreMonths();
          }
        },
        { root, rootMargin: "200px", threshold: 0 }
      );
      this.io.observe(end);
    },
    getDaysInMonth(year, month) {
      const out = [];
      const d = new Date(year, month - 1, 1);
      const todayStamp = this.getTodayStamp();

      while (d.getFullYear() === year && d.getMonth() === month - 1) {
        const dd = this.sod(d);       
        const stamp = dd.getTime();

        out.push({
          day: dd.getDate(),
          date: dd,                        
          stamp,                          
          ymd: this.toYMD(dd),           
          disabled: stamp > todayStamp,
        });
        d.setDate(d.getDate() + 1);
      }
      return out;
    },
    selectDate(date) {
      const d = this.sod(date);
      const t = d.getTime();
      if (t > this.getTodayStamp()) return;
      if (!this.startDate || this.endDate) {
        this.startDate = d; this.startStamp = t;
        this.endDate = null; this.endStamp = null;
      } else {
        if (t >= this.startStamp) {
          this.endDate = d; this.endStamp = t;
        } else {
          this.endDate = this.startDate; this.endStamp = this.startStamp;
          this.startDate = d;            this.startStamp = t;
        }
      }
    },
     isSelected(date) {
      const stamp = this.sod(date).getTime();
      return (stamp === this.startStamp || stamp === this.endStamp);
    },
     isInRange(date) {
      if (!this.startStamp || !this.endStamp) return false;
      const stamp = this.sod(date).getTime();
      const min = Math.min(this.startStamp, this.endStamp);
      const max = Math.max(this.startStamp, this.endStamp);
      return stamp > min && stamp < max;
    },

    // future flag for class binding
    isFutureStamp(stamp) {
      return stamp > this.getTodayStamp();
    },

    onKeydownMDY(e) {
      const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
      if (allowed.includes(e.key)) return;
      if (/^[0-9/]$/.test(e.key)) return; // only digits and slash
      e.preventDefault();
    },
    maskToMDY(raw) {
      const d = String(raw || '').replace(/\D/g, '').slice(0, 8);
      const mm = d.slice(0, 2);
      const dd = d.slice(2, 4);
      const yyyy = d.slice(4, 8);
      let out = mm;
      if (dd) out += '/' + dd;
      if (yyyy) out += '/' + yyyy;
      return out;
    },
    parseMDYStrict(v) {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return { error: this.formatHint };
      let [mm, dd, yyyy] = v.split('/').map(Number);
      if (mm < 1 || mm > 12) return { error: this.formatHint };
      if (String(yyyy).length !== 4) return { error: this.formatHint };
      const dim = this.daysInMonth(yyyy, mm);
      if (dd < 1 || dd > dim) return { error: this.formatHint };

      const dt = new Date(yyyy, mm - 1, dd);
      const stamp = this.sod(dt).getTime();
      const today = this.getTodayStamp();
      if (stamp > today) return { error: 'Future dates are not allowed' };
      if (this.minYearLimit && yyyy < this.minYearLimit) return { error: `Year must be ≥ ${this.minYearLimit}` };
      return { date: dt, stamp };
    },

    coerceStart() {
      const v = this.maskToMDY(this.startInput);
      this.startInput = v;

      if (!v) {
        this.startDate = null; this.startStamp = null;
        this.endInput = ''; this.endDate = null; this.endStamp = null;
        this.rangeErrors.start = 'Start date is required'; this.rangeErrors.end = '';
        return;
      }
      if (v.length < 10) { this.startDate = null; this.startStamp = null; this.rangeErrors.start = this.formatHint; return; }

      const parsed = this.parseMDYStrict(v);
      if (parsed.error) { this.startDate = null; this.startStamp = null; this.rangeErrors.start = parsed.error; return; }

      this.startDate = parsed.date; this.startStamp = parsed.stamp;
      this.rangeErrors.start = this.validateRange('start');

      // validate end if present
      if (this.endInput && this.endInput.length === 10) {
        const eParsed = this.parseMDYStrict(this.endInput);
        if (eParsed.error) { this.endDate = null; this.endStamp = null; this.rangeErrors.end = eParsed.error; }
        else { this.endDate = eParsed.date; this.endStamp = eParsed.stamp; this.rangeErrors.end = this.validateRange('end'); }
      } else { this.endDate = null; this.endStamp = null; }
    },
    coerceEnd() {
      const v = this.maskToMDY(this.endInput);
      this.endInput = v;

      if (!v) { this.endDate = null; this.endStamp = null; this.rangeErrors.end = 'End date is required'; return; }
      if (v.length < 10) { this.endDate = null; this.endStamp = null; this.rangeErrors.end = this.formatHint; return; }

      const parsed = this.parseMDYStrict(v);
      if (parsed.error) { this.endDate = null; this.endStamp = null; this.rangeErrors.end = parsed.error; return; }

      // ensure start is valid if present
      if (this.startInput && this.startInput.length === 10) {
        const sParsed = this.parseMDYStrict(this.startInput);
        if (sParsed.error) { this.startDate = null; this.startStamp = null; this.rangeErrors.start = sParsed.error; }
        else { this.startDate = sParsed.date; this.startStamp = sParsed.stamp; this.rangeErrors.start = this.validateRange('start'); }
      }

      this.endDate = parsed.date; this.endStamp = parsed.stamp;
      this.rangeErrors.end = this.validateRange('end');

      // If end typed first, do not auto-fill start; enforce explicit start entry
      if (!this.startInput) this.rangeErrors.start = 'Start date is required';
    },
    validateRange(which) {
      const today = this.getTodayStamp();

      if (which === 'start') {
        if (!this.startDate) return this.startInput ? this.formatHint : 'Start date is required';
        const s = this.startStamp;
        if (s > today) return 'Future dates are not allowed';
        // if (this.endDate && s > this.endStamp) return 'Start must be before end';
        return '';
      }
      if (which === 'end') {
        if (!this.endDate) return this.endInput ? this.formatHint : 'End date is required';
        const e = this.endStamp;
        if (e > today) return 'Future dates are not allowed';
        // if (this.startDate && e < this.startStamp) return 'End must be after start';
        return '';
      }
      return '';
    },


    formatDate(date) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    // For API: YYYY-MM-DD
    formatApiDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    saveRange() {
      if (!this.startDate) return;

      // If no endDate chosen, use startDate as both ends
      const start = this.formatApiDate(this.startDate);
      const end = this.formatApiDate(this.endDate || this.startDate);

      // Pretty label for the parent’s dropdown
      const display = `${this.formatDate(this.startDate)} → ${this.formatDate(this.endDate || this.startDate)}`;

      // Emit to parent and close
      this.$emit('apply-range', { start, end, display });
      this.onCloseCustomDate();
    },
    toggleEditRange() {
      this.isEditingRange = !this.isEditingRange;
      if (this.isEditingRange) {
        this.startInput = this.startDate ? this.toMDY(this.startDate) : '';
        this.endInput = this.endDate ? this.toMDY(this.endDate) : '';
        this.touched = { start: false, end: false };
        this.rangeErrors = { start: '', end: '' };
      }
    },
  },
};
</script>